export interface HasMixData {
  tempo: number | null
  keyName: string | null
  artistId?: number
}

// Camelot wheel positions for all 24 keys (analyzer emits sharp notation only)
const CAMELOT: Record<string, { num: number; letter: 'A' | 'B' }> = {
  'C major': { num: 8, letter: 'B' },
  'C# major': { num: 3, letter: 'B' },
  'D major': { num: 10, letter: 'B' },
  'D# major': { num: 5, letter: 'B' },
  'E major': { num: 12, letter: 'B' },
  'F major': { num: 7, letter: 'B' },
  'F# major': { num: 2, letter: 'B' },
  'G major': { num: 9, letter: 'B' },
  'G# major': { num: 4, letter: 'B' },
  'A major': { num: 11, letter: 'B' },
  'A# major': { num: 6, letter: 'B' },
  'B major': { num: 1, letter: 'B' },
  'C minor': { num: 5, letter: 'A' },
  'C# minor': { num: 12, letter: 'A' },
  'D minor': { num: 7, letter: 'A' },
  'D# minor': { num: 2, letter: 'A' },
  'E minor': { num: 9, letter: 'A' },
  'F minor': { num: 4, letter: 'A' },
  'F# minor': { num: 11, letter: 'A' },
  'G minor': { num: 6, letter: 'A' },
  'G# minor': { num: 1, letter: 'A' },
  'A minor': { num: 8, letter: 'A' },
  'A# minor': { num: 3, letter: 'A' },
  'B minor': { num: 10, letter: 'A' },
}

const CAMELOT_REVERSE: Record<string, string> = Object.fromEntries(
  Object.entries(CAMELOT).map(([key, { num, letter }]) => [`${num}${letter}`, key])
)

export function camelotLabel(keyName: string | null): string | null {
  if (!keyName) return null
  const c = CAMELOT[keyName]
  return c ? `${c.num}${c.letter}` : null
}

export function compatibleKeys(keyName: string | null): string[] {
  if (!keyName) return []
  const c = CAMELOT[keyName]
  if (!c) return []
  const { num, letter } = c
  const prev = num === 1 ? 12 : num - 1
  const next = num === 12 ? 1 : num + 1
  const opp: 'A' | 'B' = letter === 'A' ? 'B' : 'A'
  return [
    CAMELOT_REVERSE[`${prev}${letter}`],
    CAMELOT_REVERSE[`${next}${letter}`],
    CAMELOT_REVERSE[`${num}${opp}`],
  ].filter(Boolean) as string[]
}

// Steps between two Camelot positions (0=same, 1=adjacent/relative, etc.)
function harmonicDistance(a: string | null, b: string | null): number {
  if (!a || !b) return 0
  const ca = CAMELOT[a]
  const cb = CAMELOT[b]
  if (!ca || !cb) return 0
  const numDiff = Math.min(Math.abs(ca.num - cb.num), 12 - Math.abs(ca.num - cb.num))
  const letterDiff = ca.letter !== cb.letter ? 1 : 0
  return numDiff + letterDiff
}

// Normalized BPM proximity (0=identical, 1=unrelated), accounts for 2:1 octave ratio
function bpmDistance(a: number | null, b: number | null): number {
  if (!a || !b) return 0
  const direct = Math.abs(a - b) / Math.max(a, b)
  const halfA = Math.abs(a / 2 - b) / Math.max(a / 2, b)
  const doubleA = Math.abs(a * 2 - b) / Math.max(a * 2, b)
  return Math.min(direct, halfA, doubleA)
}

export const DEFAULT_HARMONIC_WEIGHT = 2
export const DEFAULT_BPM_WEIGHT = 1
export const DEFAULT_ARTIST_WEIGHT = 3

/** How many recent tracks an artist repeat is measured against. */
export const ARTIST_WINDOW = 3

export interface SequenceWeights {
  harmonic: number
  bpm: number
  artist: number
}

export const DEFAULT_SEQUENCE_WEIGHTS: SequenceWeights = {
  harmonic: DEFAULT_HARMONIC_WEIGHT,
  bpm: DEFAULT_BPM_WEIGHT,
  artist: DEFAULT_ARTIST_WEIGHT,
}

/**
 * Cost of following `recent` with `candidate`, by artist alone.
 *
 * Harmonic and BPM cost pull same-artist tracks together — one artist's catalogue
 * shares a key and a tempo range, so those transitions score as the cheapest
 * available and chain into a run. This pushes back across a window rather than
 * only at the join, decaying with distance, so repeats get spread out instead of
 * merely separated by one track. `recent` runs most-recent-first.
 */
function artistCost(recent: HasMixData[], candidate: HasMixData, weight: number): number {
  if (weight === 0 || candidate.artistId === undefined) return 0

  let cost = 0
  for (let i = 0; i < Math.min(recent.length, ARTIST_WINDOW); i++) {
    if (recent[i]!.artistId !== candidate.artistId) continue
    cost += (weight * (ARTIST_WINDOW - i)) / ARTIST_WINDOW
  }
  return cost
}

function transitionCost(recent: HasMixData[], b: HasMixData, weights: SequenceWeights): number {
  const last = recent[0]
  if (!last) return 0
  return (
    weights.harmonic * harmonicDistance(last.keyName, b.keyName) +
    weights.bpm * bpmDistance(last.tempo, b.tempo) +
    artistCost(recent, b, weights.artist)
  )
}

export interface SequenceOptions<T extends HasMixData> {
  tracks: T[]
  weights?: SequenceWeights
  /**
   * Tracks already queued ahead of this batch, oldest first. Radio appends batch
   * after batch, so without these the first pick is blind to what it follows and
   * the artist it just played comes straight back across the seam.
   */
  history?: HasMixData[]
}

/**
 * Greedy nearest-neighbor sequencer: reorders tracks to minimize total transition
 * cost — harmonic incompatibility, BPM distance, and repetition of an artist
 * played recently. With no `history` the first track is kept fixed as the seed,
 * so callers that care about the entry point should put it first.
 */
export function sequenceTracks<T extends HasMixData>({
  tracks,
  weights = DEFAULT_SEQUENCE_WEIGHTS,
  history = [],
}: SequenceOptions<T>): T[] {
  if (tracks.length <= 1) return tracks

  const recent: HasMixData[] = history.slice(-ARTIST_WINDOW).reverse()
  const result: T[] = []
  const remaining = new Set<number>()
  for (let i = 0; i < tracks.length; i++) remaining.add(i)

  // With no history there is nothing to sequence the opener against, so the
  // similarity ranking picks it and the walk starts from there.
  if (recent.length === 0) {
    result.push(tracks[0]!)
    recent.unshift(tracks[0]!)
    remaining.delete(0)
  }

  while (remaining.size > 0) {
    let bestIdx = -1
    let bestCost = Infinity

    for (const idx of remaining) {
      const cost = transitionCost(recent, tracks[idx]!, weights)
      if (cost < bestCost) {
        bestCost = cost
        bestIdx = idx
      }
    }

    const next = tracks[bestIdx]!
    result.push(next)
    recent.unshift(next)
    remaining.delete(bestIdx)
  }

  return result
}
